import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { MessageSquare, MessageCircle, GraduationCap, CalendarDays, Users, ShieldCheck, LogOut, UserRound, MapPinned } from "lucide-react";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { UserAvatar } from "@/components/UserAvatar";
import { InstallAppItem } from "@/components/InstallApp";

const TABS = [
  { to: "/", label: "Community", short: "Feed", icon: MessageSquare, id: "community", end: true },
  { to: "/groups", label: "Groups", short: "Groups", icon: MapPinned, id: "groups" },
  { to: "/classroom", label: "Classroom", short: "Learn", icon: GraduationCap, id: "classroom" },
  { to: "/events", label: "Events", short: "Events", icon: CalendarDays, id: "events" },
  { to: "/members", label: "Members", short: "Members", icon: Users, id: "members" },
  { to: "/messages", label: "Messages", short: "Chat", icon: MessageCircle, id: "messages" },
];

const BottomTab = ({ tab, badge }) => (
  <NavLink
    to={tab.to}
    end={tab.end}
    data-testid={`bottom-tab-${tab.id}`}
    className={({ isActive }) =>
      `relative flex-1 min-w-0 flex flex-col items-center gap-1 pt-2.5 pb-2 text-[10px] transition-colors ${
        isActive ? "text-[var(--hb-lime)]" : "text-white/60"
      }`
    }
  >
    <tab.icon size={20} />
    {tab.short}
    {badge > 0 && (
      <span className="absolute top-1.5 left-1/2 ml-2 rounded-full bg-[var(--hb-lime)] text-[var(--hb-ink)] text-[9px] font-bold px-1.5" data-testid={`bottom-badge-${tab.id}`}>{badge}</span>
    )}
  </NavLink>
);

const Tab = ({ tab, badge }) => (
  <NavLink
    to={tab.to}
    end={tab.end}
    data-testid={`nav-tab-${tab.id}`}
    className={({ isActive }) =>
      `relative flex items-center gap-2 px-3 md:px-4 py-3 text-sm whitespace-nowrap transition-colors ${
        isActive ? "text-[var(--hb-ink)] font-semibold" : "text-[var(--hb-muted)] hover:text-[var(--hb-ink)]"
      }`
    }
  >
    {({ isActive }) => (
      <>
        <tab.icon size={16} />
        {tab.label}
        {badge > 0 && (
          <span className="ml-1 rounded-full bg-[var(--hb-ink)] text-[var(--hb-lime)] text-[10px] font-bold px-1.5 py-0.5" data-testid={`nav-badge-${tab.id}`}>
            {tab.id === "events" ? `${badge} new` : badge}
          </span>
        )}
        <span
          className={`absolute left-2 right-2 -bottom-px h-[3px] rounded-full bg-[var(--hb-lime)] transition-transform duration-300 origin-left ${
            isActive ? "scale-x-100" : "scale-x-0"
          }`}
        />
      </>
    )}
  </NavLink>
);

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [pending, setPending] = useState(0);
  const [newEvents, setNewEvents] = useState(0);
  const [unread, setUnread] = useState(0);
  const location = useLocation();
  const isAdmin = user.role === "admin";
  const refreshUnread = () => api.get("/messages/unread-count").then((r) => setUnread(r.data.count)).catch(() => {});

  useEffect(() => {
    refreshUnread();
    const t = setInterval(refreshUnread, 30000);
    return () => clearInterval(t);
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== "/events") {
      api.get("/events/new-count").then((r) => setNewEvents(r.data.count)).catch(() => {});
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isAdmin) {
      api.get("/admin/stats").then((r) => setPending(r.data.pending)).catch(() => {});
    }
  }, [isAdmin]);

  const tabs = isAdmin ? [...TABS, { to: "/admin", label: "Admin", icon: ShieldCheck, id: "admin" }] : TABS;
  const badges = { admin: pending, events: newEvents, messages: unread };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-md border-b border-[var(--hb-line)] pt-[env(safe-area-inset-top)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <NavLink to="/" className="flex items-center gap-3" data-testid="header-logo-link">
              <img src="/brand/logo.png" alt="Hey Boo!" className="h-12 w-12 object-contain -rotate-3" />
              <div className="leading-tight">
                <div className="font-display text-lg">Hey Boo!</div>
                <div className="label-accent !text-[10px] !tracking-[.2em]">Members Only</div>
              </div>
            </NavLink>
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-trigger" className="rounded-full outline-none focus:ring-2 ring-[var(--hb-lime)]">
                <UserAvatar user={user} size={38} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white">
                <div className="px-2 py-2">
                  <div className="font-semibold text-sm" data-testid="user-menu-name">{user.name}</div>
                  <div className="text-xs text-[var(--hb-muted)] truncate">{user.email}</div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="user-menu-profile" onClick={() => navigate("/profile")}>
                  <UserRound size={15} className="mr-2" /> My profile
                </DropdownMenuItem>
                <InstallAppItem />
                {isAdmin && (
                  <DropdownMenuItem className="md:hidden" data-testid="user-menu-admin" onClick={() => navigate("/admin")}>
                    <ShieldCheck size={15} className="mr-2" /> Admin{pending > 0 ? ` (${pending})` : ""}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="user-menu-logout" onClick={logout}>
                  <LogOut size={15} className="mr-2" /> Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <nav className="hidden md:flex overflow-x-auto no-scrollbar -mx-2" data-testid="main-nav">
            {tabs.map((t) => (
              <Tab key={t.id} tab={t} badge={badges[t.id] || 0} />
            ))}
          </nav>
        </div>
      </header>
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 md:py-8 pb-28 md:pb-8">
        <Outlet context={{ setPending, setNewEvents, refreshUnread }} />
      </main>
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-[var(--hb-ink)] flex pb-[env(safe-area-inset-bottom)]"
        data-testid="bottom-nav"
      >
        {TABS.map((t) => (
          <BottomTab key={t.id} tab={t} badge={badges[t.id] || 0} />
        ))}
      </nav>
    </div>
  );
}
